<script setup lang="ts">
/**
 * A small state label.
 *
 * `state` maps onto the shared colour map in `styles/utilities.css`, so `ok`, `up` and `done` are
 * the same green everywhere without any component deciding for itself what green means.
 */
withDefaults(
  defineProps<{
    state?:
      | 'ok'
      | 'warning'
      | 'critical'
      | 'unknown'
      | 'info'
      | 'neutral'
      | 'up'
      | 'down'
      | 'suspect'
      | 'recovering'
      | 'queued'
      | 'running'
      | 'done'
      | 'error'
      | 'blocked'
      | 'degraded'
      | 'failing'
      | 'unreachable'
      | 'accent'
      | 'success'
    variant?: 'soft' | 'outline' | 'dot'
    /** Machine-ish content — a capability id, a slug — gets the monospace face. */
    mono?: boolean
    title?: string
  }>(),
  { state: 'neutral', variant: 'soft', mono: false },
)
</script>

<template>
  <span class="o-badge" :class="[`v-${variant}`, { mono }]" :data-state="state" :title="title">
    <span v-if="variant === 'dot'" class="dot" aria-hidden="true" />
    <slot />
  </span>
</template>

<style scoped>
.o-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  border-radius: var(--radius-full);
  padding: 0.125rem var(--space-2);
  font-size: var(--text-2xs);
  font-weight: var(--weight-semibold);
  letter-spacing: var(--tracking-wide);
  line-height: 1.5;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-soft {
  background: var(--state-bg);
  color: var(--state-fg);
}

.v-outline {
  border: var(--border-width) solid var(--state-border);
  color: var(--state-fg);
  background: transparent;
}

.v-dot {
  background: transparent;
  color: var(--text-default);
  padding-inline: 0;
  font-weight: var(--weight-medium);
  letter-spacing: 0;
}

.dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: var(--radius-full);
  background: var(--state-fg);
  flex: none;
}

.mono {
  font-family: var(--font-mono);
  letter-spacing: 0;
  text-transform: none;
}
</style>
